/**
 * Auth0 Plugin for Vue 2 with Vuex Integration
 * 
 * This plugin integrates Auth0 with Vuex store instead of creating a separate Vue instance.
 * It provides a cleaner separation of concerns and better state management.
 */

import Vue from "vue";

/**
 * Vue.js Plugin Definition
 */
export const Auth0Plugin = {
  install(Vue, options) {
    // Add Auth0 methods to Vue prototype for easy access
    Vue.prototype.$auth0 = {
      // Login with redirect
      login(options = {}) {
        return this.$store.dispatch('AUTH0_LOGIN', options)
      },
      
      // Logout
      logout(options = {}) {
        return this.$store.dispatch('AUTH0_LOGOUT', options)
      },
      
      // Get access token silently
      getToken(options = {}) {
        return this.$store.dispatch('AUTH0_GET_TOKEN', options)
      },
      
      // Get Auth0 client instance
      getClient() {
        return this.$store.getters.auth0Client
      },
      
      // Check if authenticated
      isAuthenticated() {
        return this.$store.getters.auth0IsAuthenticated
      },
      
      // Get current user
      getUser() {
        return this.$store.getters.auth0User
      },
      
      // Check if loading
      isLoading() {
        return this.$store.getters.auth0IsLoading
      },
      
      // Get error
      getError() {
        return this.$store.getters.auth0Error
      }
    }
  }
}

/**
 * Initialize Auth0 with the given options
 * This should be called in main.js after the store is created
 */
export const initializeAuth0 = async (store, options) => {
  const {
    domain,
    clientId,
    redirectUri = window.location.origin,
    audience,
    onRedirectCallback = () => {
      window.history.replaceState({}, document.title, window.location.pathname)
    }
  } = options

  try {
    // Initialize Auth0 client
    const appState = await store.dispatch('INITIALIZE_AUTH0', {
      domain,
      clientId,
      redirectUri,
      audience
    })
    
    // Call redirect callback only if we have a valid app state from a callback
    if (appState && typeof appState === 'object' && appState.targetPath !== undefined) {
      onRedirectCallback(appState)
    }
    
    return true
  } catch (error) {
    console.error('Failed to initialize Auth0:', error)
    return false
  }
}

/**
 * Get Auth0 client instance (for backward compatibility)
 */
export const getAuth0Client = () => {
  // This function is kept for backward compatibility
  // In the new implementation, components should use this.$auth0.getClient()
  // or this.$store.getters.auth0Client
  console.warn('getAuth0Client() is deprecated. Use this.$auth0.getClient() or this.$store.getters.auth0Client instead.')
  return null
}